import * as Yup from 'yup';
import FormSectionPage from '../form-section/formSectionPage';
import i18n from 'src/localization/i18n';
import Input from 'src/components/forms/input';
import { useFormik } from 'formik';
import { VEHICLEINFO_VIN_ERROR } from 'src/localization/resouceKeys';
import React, { useEffect, useMemo, useState } from 'react';

interface IAccountInformation {
    customerName: string;
    accountNumber: string;
    vin: string;
}

interface IProps {
    onSave: (values: IAccountInformation) => void;
    isEnabled?: boolean;
    onFormTouched?: () => void;
}

const CUSTOMER_NAME_MAX_LENGTH = 30;
const CUSTOMER_NAME_REGEX = /^[A-Za-z '-]+$/;

const AccountInformation: React.FC<IProps> = ({
    onSave,
    isEnabled = false,
    onFormTouched,
}) => {
    const VIN_REGEX = /^[a-hj-npr-zA-HJ-NPR-Z0-9]{17}$/i;
    const [isOpen, setOpen] = useState<boolean>(false);
    const [showIcon, setShowIcon] = useState<boolean | null>(null);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);

    const schema = useMemo(
        () =>
            Yup.object({
                customerName: Yup.string()
                    .trim()
                    .max(
                        CUSTOMER_NAME_MAX_LENGTH,
                        // eslint-disable-next-line quotes
                        "Customer name should not exceed 30 Character's.",
                    )
                    .matches(CUSTOMER_NAME_REGEX, {
                        message:
                            'Customer name may only contain letters, spaces, hyphens, and apostrophes.',
                        excludeEmptyString: true,
                    })
                    .required('Field required'),
                accountNumber: Yup.string().matches(/^\d{9,14}$/, {
                    message: 'Account Numbers must be between 9 and 14 digits.',
                    excludeEmptyString: true,
                }),
                vin: Yup.string()
                    .required(() => i18n.t(VEHICLEINFO_VIN_ERROR))
                    .matches(VIN_REGEX, () => i18n.t(VEHICLEINFO_VIN_ERROR)),
            }),
        [],
    );

    const formik = useFormik<IAccountInformation>({
        initialValues: {
            customerName: '',
            accountNumber: '',
            vin: '',
        },
        validationSchema: schema,
        validateOnMount: true,
        onSubmit: (values) => {
            onSave(values);
            setShowIcon(true);
            setOpen(false);
            setIsDisabled(true);
        },
    });

    useEffect(() => {
        if (formik.dirty && !isDisabled && isEnabled) {
            onFormTouched?.();
        }
    }, [formik.dirty, isDisabled, isEnabled, onFormTouched]);

    const hasError = (field: keyof IAccountInformation) =>
        !!(formik.touched[field] && formik.errors[field]);

    const handleCustomerNameChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const value = event.currentTarget.value.replace(/[^A-Za-z '\-]/g, '');
        formik.setFieldValue('customerName', value);
    };

    const handleAccountNumberChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        formik.setFieldValue(
            'accountNumber',
            event.currentTarget.value.replace(/\D/g, ''),
        );
    };

    const handleVinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue(
            'vin',
            event.currentTarget.value
                .replace(/[^a-zA-Z0-9]/g, '')
                .toUpperCase(),
        );
    };

    const handleEdit = () => {
        setIsDisabled(false);
        setOpen(true);
        setShowIcon(null);
        formik.setTouched({});
        formik.setErrors({});
    };

    useEffect(() => {
        if (isEnabled) {
            setOpen(true);
        }
    }, [isEnabled]);

    return (
        <FormSectionPage
            title={'Account Information'}
            handlerSubmit={formik.submitForm}
            isOpen={isOpen}
            setOpen={setOpen}
            showIcon={showIcon}
            isDisabled={isDisabled}
            onEdit={handleEdit}
            isFormValid={formik.isValid}
            isBlocked={!isEnabled}
        >
            <div className='o-layout'>
                <div className='o-layout__item u-1/2 u-1/1@xs'>
                    <Input
                        type='text'
                        name='customerName'
                        labelName={'Customer Name*'}
                        value={formik.values.customerName}
                        handlerChange={handleCustomerNameChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={
                            hasError('customerName')
                                ? formik.errors.customerName
                                : ''
                        }
                        asTouched={formik.touched.customerName}
                        disabled={isDisabled || !isEnabled}
                        maxlength={CUSTOMER_NAME_MAX_LENGTH}
                    />
                </div>
                <div className='o-layout__item u-1/2 u-1/1@xs'>
                    <Input
                        type='tel'
                        name='accountNumber'
                        labelName={'Account Number'}
                        value={formik.values.accountNumber}
                        handlerChange={handleAccountNumberChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={
                            hasError('accountNumber')
                                ? formik.errors.accountNumber
                                : ''
                        }
                        asTouched={formik.touched.accountNumber}
                        disabled={isDisabled || !isEnabled}
                        maxlength={14}
                    />
                </div>
                <div className='o-layout__item u-1/2 u-1/1@xs'>
                    <Input
                        type='text'
                        name='vin'
                        labelName={'VIN*'}
                        value={formik.values.vin}
                        handlerChange={handleVinChange}
                        handlerBlur={formik.handleBlur}
                        asErrors={hasError('vin') ? formik.errors.vin : ''}
                        asTouched={formik.touched.vin}
                        disabled={isDisabled || !isEnabled}
                        maxlength={17}
                    />
                </div>
            </div>
        </FormSectionPage>
    );
};

export default AccountInformation;
